import React from 'react';
import { useNavigation } from '../Navigation/NavigationContext';
import { navigationData } from '../../data/navigationData';
import Breadcrumbs from './Breadcrumbs';
import './MainContent.css';

const MainContent: React.FC = () => {
  const { state } = useNavigation();

  const getPageContent = () => {
    let title = 'Welcome to Modernize';
    let description = 'Navigate through our products to see the multi-level navigation system in action.';
    let contentTitle = 'Select a product to see detailed information';
    let contentDescription = 'Navigate using the menu system above to explore different products and features.';

    if (state.section === 'homeowners') {
      title = 'For Homeowners';
      description = 'Find trusted professionals for your home improvement projects.';
      contentTitle = 'Homeowner Services';
      contentDescription = 'Get quotes from qualified contractors in your area and manage your home improvement projects with confidence.';
    } else if (state.section === 'pros') {
      title = 'For Professionals';
      description = 'Grow your business with our comprehensive suite of professional tools.';
      contentTitle = 'Professional Services';
      contentDescription = 'Access our full range of business growth tools including leads, calls, financing, and more.';
      
      if (state.product) {
        const productData = navigationData.products[state.product];
        if (productData) {
          title = productData.title;
          description = `Explore ${productData.title} features and capabilities.`;
          contentTitle = `${productData.title} - ${getCurrentPageTitle()}`;
          contentDescription = getProductDescription(state.product, state.page);
        }
      }
    } else if (state.section === 'about') {
      title = 'About Modernize';
      description = 'Learn more about our company and mission.';
      contentTitle = 'About Our Company';
      contentDescription = 'Modernize is dedicated to connecting homeowners with qualified professionals and providing tools for business growth.';
    }

    return { title, description, contentTitle, contentDescription };
  };

  const getCurrentPageTitle = () => {
    if (!state.product || !state.page) return 'Overview';
    
    const productData = navigationData.products[state.product];
    if (!productData) return 'Overview';
    
    const pageItem = productData.items.find(item => {
      const pathParts = item.href.split('/');
      const pageName = pathParts[pathParts.length - 1] || 'overview';
      return pageName === state.page;
    });
    
    return pageItem ? pageItem.label : 'Overview';
  };

  const getProductDescription = (product: string, page: string | null) => {
    const descriptions: Record<string, Record<string, string>> = {
      'finance': {
        'overview': 'Comprehensive financing solutions to help contractors offer flexible payment options to their customers.',
        'features': 'Advanced features including instant approvals, flexible terms, and seamless integration.',
        'pricing': 'Competitive pricing structure with transparent fees and no hidden costs.'
      },
      'leads': {
        'overview': 'Generate high-quality leads for your business with our proven lead generation system.',
        'branded': 'Get leads that are specifically branded to your company for better conversion.',
        'live-transfer': 'Receive leads via live phone transfer for immediate engagement.',
        'qualified': 'Access pre-qualified leads that have been vetted for serious intent.',
        'pricing': 'Flexible pricing options to match your budget and business needs.'
      },
      'calls': {
        'overview': 'Professional call handling services to ensure you never miss a potential customer.',
        'inbound': 'Dedicated inbound call handling for your business inquiries.',
        'live-transfer': 'Live transfer services to connect you with interested prospects.',
        'pricing': 'Affordable call service pricing with pay-per-call options.'
      },
      'programs': {
        'overview': 'Branded program solutions to enhance your business credibility and reach.',
        'benefits': 'Learn about the exclusive benefits of joining our branded programs.',
        'getting-started': 'Step-by-step guide to getting started with our branded programs.',
        'pricing': 'Transparent pricing for our branded program offerings.'
      }
    };
    
    return descriptions[product]?.[page || 'overview'] || 'Detailed information about this product feature.';
  };

  const content = getPageContent();

  return (
    <main className="main-content">
      <div className="container">
        <section className="hero">
          <h1>{content.title}</h1>
          <p>{content.description}</p>
          
          <Breadcrumbs />
        </section>

        {/* Demo Instructions */}
        <section className="demo-instructions card">
          <div className="card__body">
            <h2>Navigation Demo Instructions</h2>
            <ol>
              <li>Use the <strong>floating control button</strong> (bottom right) to toggle demo features</li>
              <li>Enable <strong>"Products bar above site navigation"</strong> to move products to the top</li>
              <li>Click on <strong>"For Pros"</strong> to see the navigation hierarchy</li>
              <li>Click on any product icon to navigate and see active states</li>
              <li>Enable <strong>"Show product logos"</strong> to add icons to sub-navigation</li>
              <li>Try the mobile menu by clicking the hamburger icon on smaller screens</li>
              <li>Use the search functionality in the main navigation</li>
            </ol>
          </div>
        </section>

        {/* Product Content Area */}
        <section className="product-content">
          <div className="card">
            <div className="card__body">
              <h2>{content.contentTitle}</h2>
              <p>{content.contentDescription}</p>
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default MainContent;